import React, { useState } from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, FileTextIcon, BuildingIcon, ShieldIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
interface ConstructionPaymentReportProps {
  filters: {
    dateRange: string;
    startDate: string;
    endDate: string;
  };
  locale?: string;
}
export function ConstructionPaymentReport({
  filters,
  locale = 'en-US'
}: ConstructionPaymentReportProps) {
  const [selectedTab, setSelectedTab] = useState('overview');
  // Sample data for Security of Payment
  const sopClaims = [{
    id: 'SOP-2023-001',
    claimNumber: 'PC-001',
    contractor: 'BuildCorp Pty Ltd',
    project: 'City Tower Development',
    claimDate: '2023-11-01',
    dueDate: '2023-11-15',
    amount: 450000,
    status: 'Payment Due',
    daysRemaining: 5,
    retentionAmount: 22500,
    progressPercentage: 45
  }, {
    id: 'SOP-2023-002',
    claimNumber: 'PC-002',
    contractor: 'Steel Solutions Ltd',
    project: 'Industrial Complex',
    claimDate: '2023-10-28',
    dueDate: '2023-11-12',
    amount: 280000,
    status: 'Under Review',
    daysRemaining: 2,
    retentionAmount: 14000,
    progressPercentage: 60
  }, {
    id: 'SOP-2023-003',
    claimNumber: 'PC-003',
    contractor: 'Electrical Contractors Inc',
    project: 'Retail Plaza',
    claimDate: '2023-10-25',
    dueDate: '2023-11-08',
    amount: 125000,
    status: 'Overdue',
    daysRemaining: -2,
    retentionAmount: 6250,
    progressPercentage: 75
  }, {
    id: 'SOP-2023-004',
    claimNumber: 'PC-004',
    contractor: 'Plumbing Services Co',
    project: 'Residential Complex',
    claimDate: '2023-11-05',
    dueDate: '2023-11-20',
    amount: 95000,
    status: 'Approved',
    daysRemaining: 10,
    retentionAmount: 4750,
    progressPercentage: 30
  }];
  // Calculate summary statistics
  const totalClaimValue = sopClaims.reduce((sum, claim) => sum + claim.amount, 0);
  const totalRetention = sopClaims.reduce((sum, claim) => sum + claim.retentionAmount, 0);
  const overdueClaims = sopClaims.filter(claim => claim.daysRemaining < 0).length;
  const upcomingPayments = sopClaims.filter(claim => claim.daysRemaining > 0 && claim.daysRemaining <= 7).length;
  // Project summary data
  const projectSummary = [{
    project: 'City Tower Development',
    contractValue: 10000000,
    claimedToDate: 4500000,
    paidToDate: 4050000,
    retention: 225000,
    progressPercentage: 45
  }, {
    project: 'Industrial Complex',
    contractValue: 5500000,
    claimedToDate: 3300000,
    paidToDate: 2970000,
    retention: 165000,
    progressPercentage: 60
  }, {
    project: 'Retail Plaza',
    contractValue: 3200000,
    claimedToDate: 2400000,
    paidToDate: 2160000,
    retention: 120000,
    progressPercentage: 75
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Payment Due':
        return 'bg-blue-100 text-blue-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-15888">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-15889">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15890">
          <div className="p-5" data-id="element-15891">
            <div className="flex items-center" data-id="element-15892">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-15893">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15894" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15895">
                <dl data-id="element-15896">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15897">
                    Total Claim Value
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15898">
                    {formatCurrency(totalClaimValue, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15899">
          <div className="p-5" data-id="element-15900">
            <div className="flex items-center" data-id="element-15901">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-15902">
                <ShieldIcon className="h-6 w-6 text-white" data-id="element-15903" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15904">
                <dl data-id="element-15905">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15906">
                    Total Retention
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15907">
                    {formatCurrency(totalRetention, 'USD', locale)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15908">
          <div className="p-5" data-id="element-15909">
            <div className="flex items-center" data-id="element-15910">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-3" data-id="element-15911">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-15912" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15913">
                <dl data-id="element-15914">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15915">
                    Overdue Claims
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15916">
                    {overdueClaims}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15917">
          <div className="p-5" data-id="element-15918">
            <div className="flex items-center" data-id="element-15919">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15920">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-15921" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15922">
                <dl data-id="element-15923">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15924">
                    Due This Week
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-15925">
                    {upcomingPayments}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white shadow rounded-lg" data-id="element-15926">
        <div className="border-b border-gray-200" data-id="element-15927">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-15928">
            <button onClick={() => setSelectedTab('overview')} className={`${selectedTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15929">
              Security of Payment
            </button>
            <button onClick={() => setSelectedTab('projects')} className={`${selectedTab === 'projects' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15930">
              Project Summary
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-15931">
          {selectedTab === 'overview' && <div className="space-y-6" data-id="element-15932">
              <div className="flex justify-between items-center" data-id="element-15933">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-15934">
                  Security of Payment Claims
                </h3>
                <div className="text-sm text-gray-500" data-id="element-15935">
                  Showing {sopClaims.length} active claims
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-15936">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-15937">
                  <thead className="bg-gray-50" data-id="element-15938">
                    <tr data-id="element-15939">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15940">
                        Claim #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15941">
                        Contractor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15942">
                        Project
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15943">
                        Claim Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15944">
                        Retention
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15945">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15946">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15947">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-15948">
                    {sopClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50" data-id="element-15949">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-15950">
                          {claim.claimNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15951">
                          {claim.contractor}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15952">
                          {claim.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-15953">
                          {formatCurrency(claim.amount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15954">
                          {formatCurrency(claim.retentionAmount, 'USD', locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15955">
                          <div className="flex items-center" data-id="element-15956">
                            <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-15957" />
                            {new Date(claim.dueDate).toLocaleDateString(locale)}
                            {claim.daysRemaining < 0 && <span className="ml-2 text-red-600 font-medium" data-id="element-15958">
                                ({Math.abs(claim.daysRemaining)} days overdue)
                              </span>}
                            {claim.daysRemaining >= 0 && claim.daysRemaining <= 7 && <span className="ml-2 text-yellow-600 font-medium" data-id="element-15959">
                                  ({claim.daysRemaining} days)
                                </span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15960">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(claim.status)}`} data-id="element-15961">
                            {claim.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15962">
                          <div className="flex items-center" data-id="element-15963">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2" data-id="element-15964">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                          width: `${claim.progressPercentage}%`
                        }} data-id="element-15965"></div>
                            </div>
                            <span className="text-sm text-gray-500" data-id="element-15966">
                              {claim.progressPercentage}%
                            </span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {selectedTab === 'projects' && <div className="space-y-6" data-id="element-15967">
              <div className="flex justify-between items-center" data-id="element-15968">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-15969">
                  Project Financial Summary
                </h3>
              </div>
              <div className="space-y-4" data-id="element-15970">
                {projectSummary.map((project, index) => <div key={index} className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-15971">
                    <div className="flex justify-between items-start" data-id="element-15972">
                      <div data-id="element-15973">
                        <h4 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-15974">
                          <BuildingIcon className="h-5 w-5 mr-2 text-indigo-600" data-id="element-15975" />
                          {project.project}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-15976">
                          Contract Value:{' '}
                          {formatCurrency(project.contractValue, 'USD', locale)}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-15977">
                        <div className="text-2xl font-bold text-gray-900" data-id="element-15978">
                          {project.progressPercentage}%
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-15979">Complete</div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-15980">
                      <div className="bg-indigo-600 h-3 rounded-full" style={{
                  width: `${project.progressPercentage}%`
                }} data-id="element-15981"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-15982">
                      <div data-id="element-15983">
                        <div className="text-sm text-gray-500" data-id="element-15984">
                          Claimed to Date
                        </div>
                        <div className="text-lg font-semibold text-gray-900" data-id="element-15985">
                          {formatCurrency(project.claimedToDate, 'USD', locale)}
                        </div>
                      </div>
                      <div data-id="element-15986">
                        <div className="text-sm text-gray-500" data-id="element-15987">
                          Paid to Date
                        </div>
                        <div className="text-lg font-semibold text-gray-900" data-id="element-15988">
                          {formatCurrency(project.paidToDate, 'USD', locale)}
                        </div>
                      </div>
                      <div data-id="element-15989">
                        <div className="text-sm text-gray-500" data-id="element-15990">
                          Retention Held
                        </div>
                        <div className="text-lg font-semibold text-yellow-600" data-id="element-15991">
                          {formatCurrency(project.retention, 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}